import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Schedule from "@/pages/Schedule";
import {
    CalendarBlank,
    VideoCamera,
    MapPin,
    Phone,
    ArrowSquareOut,
    User,
    Buildings,
    ListBullets,
    Calendar,
} from "@phosphor-icons/react";

const SCOPES = [
    { id: "upcoming", label: "Upcoming" },
    { id: "past", label: "Past" },
    { id: "all", label: "All" },
];

const TYPE_META = {
    virtual: { label: "Virtual", Icon: VideoCamera, color: "text-cobalt" },
    face_to_face: { label: "In-person", Icon: MapPin, color: "text-splash-dark" },
    telephonic: { label: "Phone", Icon: Phone, color: "text-[#525252]" },
};

export default function InterviewsDashboard() {
    const [view, setView] = useState("list");   // list | calendar
    const [scope, setScope] = useState("upcoming");
    const [items, setItems] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (view !== "list") return;
        setItems(null);
        setErr("");
        api
            .get("/interviews", { params: { scope } })
            .then((r) => setItems(r.data.items || []))
            .catch((ex) => setErr(formatApiError(ex.response?.data?.detail) || ex.message));
    }, [scope, view]);

    const fmt = (iso) =>
        new Date(iso).toLocaleString(undefined, {
            weekday: "short",
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });

    return (
        <div className="p-4 sm:p-8 max-w-[1600px] mx-auto">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                        Schedule
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
                        Interviews
                    </h1>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex border border-[#E5E5E5] bg-white" data-testid="interviews-view-toggle">
                        <button
                            data-testid="view-list"
                            onClick={() => setView("list")}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                                view === "list" ? "bg-ink text-white" : "text-[#525252] hover:bg-[#F5F5F5]"
                            }`}
                        >
                            <ListBullets size={12} weight="bold" /> List
                        </button>
                        <button
                            data-testid="view-calendar"
                            onClick={() => setView("calendar")}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                                view === "calendar" ? "bg-ink text-white" : "text-[#525252] hover:bg-[#F5F5F5]"
                            }`}
                        >
                            <Calendar size={12} weight="bold" /> Calendar
                        </button>
                    </div>
                    {view === "list" && (
                        <div className="flex border border-[#E5E5E5] bg-white" data-testid="interviews-scope-tabs">
                            {SCOPES.map((s) => (
                                <button
                                    key={s.id}
                                    data-testid={`scope-${s.id}`}
                                    onClick={() => setScope(s.id)}
                                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                                        scope === s.id
                                            ? "bg-ink text-white"
                                            : "text-[#525252] hover:bg-[#F5F5F5]"
                                    }`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {view === "calendar" ? (
                <Schedule />
            ) : (
                <>
                {err && (
                <div className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3 mb-4">
                    {err}
                </div>
            )}

            {items == null ? (
                <div className="text-sm text-[#525252]">Loading…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-[#E5E5E5] p-12 text-center bg-white">
                    <CalendarBlank size={32} weight="bold" className="text-[#A3A3A3] mx-auto mb-3" />
                    <p className="font-heading text-lg font-bold tracking-tight">
                        No {scope === "upcoming" ? "upcoming" : scope === "past" ? "past" : ""} interviews
                    </p>
                    <p className="text-sm text-[#525252] mt-1">
                        Schedule one from a candidate at the Interview stage.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4" data-testid="interviews-grid">
                    {items.map((iv) => {
                        const meta = TYPE_META[iv.interview_type] || TYPE_META.virtual;
                        const Icon = meta.Icon;
                        return (
                            <div
                                key={iv.event_id}
                                data-testid={`iv-card-${iv.event_id}`}
                                className="bg-white border border-[#E5E5E5] hover:border-ink transition-colors p-4 sm:p-5"
                            >
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <div className="min-w-0 flex-1">
                                        <div className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider ${meta.color} mb-2`}>
                                            <Icon size={11} weight="bold" /> {meta.label}
                                        </div>
                                        <p className="font-heading text-lg font-bold tracking-tight leading-tight truncate">
                                            {iv.candidate_name}
                                        </p>
                                        <p className="text-xs text-[#525252] mt-0.5 truncate">
                                            {iv.job_title || "—"}
                                            {iv.client_name ? (
                                                <>
                                                    {" "}
                                                    <span className="text-cobalt inline-flex items-center gap-1">
                                                        · <Buildings size={10} weight="bold" /> {iv.client_name}
                                                    </span>
                                                </>
                                            ) : null}
                                        </p>
                                    </div>
                                    <Link
                                        to={`/requirements/${iv.job_id}`}
                                        className="text-[10px] font-bold uppercase tracking-wider text-cobalt hover:underline whitespace-nowrap"
                                    >
                                        Open →
                                    </Link>
                                </div>
                                <p className="text-sm font-bold text-ink">{fmt(iv.start)}</p>
                                <p className="text-[11px] text-[#A3A3A3] mb-3">{iv.timezone}</p>
                                {iv.location && (
                                    <p className="text-xs text-[#525252] flex items-start gap-1">
                                        <MapPin size={12} weight="bold" className="mt-0.5 shrink-0" />
                                        <span className="truncate">{iv.location}</span>
                                    </p>
                                )}
                                {iv.phone_number && (
                                    <p className="text-xs text-[#525252] flex items-center gap-1">
                                        <Phone size={12} weight="bold" /> {iv.phone_number}
                                    </p>
                                )}
                                {iv.hiring_manager_name && (
                                    <p className="text-xs text-[#525252] flex items-center gap-1 mt-1">
                                        <User size={12} weight="bold" /> With {iv.hiring_manager_name}
                                    </p>
                                )}
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {iv.meet_link && (
                                        <a
                                            href={iv.meet_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn-gradient px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1"
                                        >
                                            <VideoCamera size={11} weight="bold" /> Join Meet
                                        </a>
                                    )}
                                    {iv.html_link && (
                                        <a
                                            href={iv.html_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="border border-[#E5E5E5] hover:border-ink px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1 text-[#525252] hover:text-ink transition-colors"
                                        >
                                            <ArrowSquareOut size={11} weight="bold" /> Calendar
                                        </a>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
                </>
            )}
        </div>
    );
}
